/**
 * クライアント側の異常をサーバに知らせる（SRE 画面側 3・4）。
 *
 * 送り先は POST /api/client-events（ログイン済みのみ、1 利用者あたり毎分 10 件まで、本文 8KB まで）。
 * **個人情報や入力値は送らない。** 送るのはメッセージ・画面名・版・requestId・指標の数値だけ。
 * サーバがまだ受け口を持っていなくても（404 等）、黙って捨てる。送信の失敗で画面を止めない。
 */
import { appVersion } from "./api.js";

interface ClientEvent {
  kind: "error" | "vitals";
  page: string;
  message?: string;
  requestId?: string;
  metrics?: Record<string, number>;
}

const MAX_PER_PAGELOAD = 10;
let sent = 0;
let signedIn = false;

/** ログイン中だけ送る（未認証で送っても 401 になるだけなので送らない）。 */
export function setTelemetrySignedIn(v: boolean): void { signedIn = v; }

/** 画面名だけを返す（ID や検索語はクエリ・パスから落とす）。 */
export function currentPage(): string {
  const h = location.hash.split("?")[0] ?? "";
  return h.replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id").slice(0, 80) || "#/";
}

export function sendClientEvent(ev: ClientEvent): void {
  if (!signedIn || sent >= MAX_PER_PAGELOAD) return;
  sent++;
  const body = JSON.stringify({
    ...ev,
    message: ev.message?.slice(0, 500),
    appVersion: appVersion() ?? "unknown",
  });
  try {
    void fetch("/api/client-events", {
      method: "POST", credentials: "same-origin", keepalive: true,
      headers: { "content-type": "application/json" }, body,
    }).catch(() => { /* 受け口が無い・通信できない: 捨てる */ });
  } catch { /* 捨てる */ }
}

/** 例外を1行のメッセージにする（スタックや入力値は送らない）。 */
export function errorMessage(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`.slice(0, 300);
  return String(err).slice(0, 300);
}

// ── Web Vitals（LCP・INP・CLS）。悪いときだけ1回送る ────────────────
const LIMITS = { lcp: 2500, inp: 200, cls: 0.1 } as const;

export function observeVitals(): void {
  if (typeof PerformanceObserver === "undefined") return;
  const supported = PerformanceObserver.supportedEntryTypes ?? [];
  const m: { lcp?: number; inp?: number; cls?: number } = {};

  const watch = (type: string, cb: (entries: PerformanceEntryList) => void, extra: Record<string, unknown> = {}) => {
    if (!supported.includes(type)) return;
    try { new PerformanceObserver((l) => cb(l.getEntries())).observe({ type, buffered: true, ...extra } as PerformanceObserverInit); } catch { /* 古いブラウザ */ }
  };

  watch("largest-contentful-paint", (es) => { const last = es[es.length - 1]; if (last) m.lcp = last.startTime; });

  // CLS: 直近の入力から 500ms 以内を除いたずれを、1 秒以内の間隔・5 秒以内の窓で束ねた最大値
  let win = 0; let winStart = 0; let prev = 0;
  watch("layout-shift", (es) => {
    for (const e of es as Array<PerformanceEntry & { value: number; hadRecentInput: boolean }>) {
      if (e.hadRecentInput) continue;
      if (e.startTime - prev > 1000 || e.startTime - winStart > 5000) { win = 0; winStart = e.startTime; }
      win += e.value; prev = e.startTime;
      m.cls = Math.max(m.cls ?? 0, win);
    }
  });

  // INP（近似）: 操作ごと（interactionId）の最大の所要時間
  watch("event", (es) => {
    for (const e of es as Array<PerformanceEntry & { interactionId?: number }>) {
      if (!e.interactionId) continue;
      m.inp = Math.max(m.inp ?? 0, e.duration);
    }
  }, { durationThreshold: 40 });

  let reported = false;
  const report = () => {
    if (reported) return;
    const bad = (m.lcp ?? 0) > LIMITS.lcp || (m.inp ?? 0) > LIMITS.inp || (m.cls ?? 0) > LIMITS.cls;
    if (!bad) return;
    reported = true;
    const metrics: Record<string, number> = {};
    if (m.lcp !== undefined) metrics["lcp"] = Math.round(m.lcp);
    if (m.inp !== undefined) metrics["inp"] = Math.round(m.inp);
    if (m.cls !== undefined) metrics["cls"] = Math.round(m.cls * 1000) / 1000;
    sendClientEvent({ kind: "vitals", page: currentPage(), metrics });
  };
  addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") report(); });
  addEventListener("pagehide", report);
}
